/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import ChipInput from 'material-ui-chip-input';
import { Trans, useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Paper, Typography } from '@material-ui/core';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import AssistiveText from './AssistiveText';
import { text } from '../../../shared/validations/standardChecks';
import Tooltip from '../../common/Tooltip';
import AttachmentUploader from '../../draft/attachments/uploader/AttachmentUploader';

const useStyles = makeStyles(theme => ({
  tooltip: {
    position: 'absolute',
    marginLeft: '0.75rem',
    marginTop: '1rem',
  },
  chipInput: {
    '& .MuiChip-clickable': {
      overflow: 'auto',
    },
  },
  ipcCodes: {
    [theme.breakpoints.up('sm')]: {
      width: '95%',
    },
    [theme.breakpoints.up('md')]: {
      width: 'inherit',
    },
  },
  paper: {
    margin: '1.5rem 0',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',
  },
  titleText: {
    color: theme.palette.titleText,
    paddingBottom: '2rem',
    textAlign: 'center',
  },
}));

const ChipsField = ({ formData, schema, uiSchema, disabled, rawErrors, label, onChange }) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);

  const classes = useStyles();
  const isDisabled = uiSchema?.['ui:disabled'] || disabled;
  const displayLabel = uiSchema?.[uiSchemaProperties.UI_TITLE]
    ? (uiSchema?.title && t(uiSchema?.config?.title)) || label || schema.title
    : false;

  const [ipcCodes, setIpcCodes] = useState(formData || []);
  const tooltip = schema?.config?.tooltip ?? uiSchema?.[uiSchemaProperties.UI_TOOLTIP];
  const separator = uiSchema?.[uiSchemaProperties.UI_SEPARATOR];
  const fileImport = uiSchema?.[uiSchemaProperties.UI_FILE_IMPORT];

  const handleAddChip = (chip) => {
    setErrorMessage(null);
    const lengthValidation = text(0, schema?.items?.maxLength)(chip);
    if (!lengthValidation) {
      setErrorMessage(lengthValidation);
      return;
    }

    const chipArr = separator ? chip?.split(separator) : [chip];
    chipArr.forEach((_chip) => {
      const patternValidation = schema?.items?.pattern != null ? new RegExp(schema?.items?.pattern).test(_chip) : false;
      if (!patternValidation) {
        setErrorMessage(t(uiSchema?.['ui:validationPatternMessage']));
        return;
      }
      ipcCodes.push(_chip?.trim());
    });
    onChange(ipcCodes);
    setIpcCodes(ipcCodes);
  };

  const handleDeleteChip = (chip, index) => {
    ipcCodes.splice(index, 1);
    setIpcCodes(ipcCodes);
    onChange(ipcCodes);
  };

  const handleCsvImport = async (droppedFiles, onRemoveDroppedFile) => {
    const csvContent = await droppedFiles[0].text();
    const importedCodes = csvContent.split(new RegExp(`[\r\n]+|${fileImport.separator}`, 'g'));

    let patternValidation = null;
    if (schema?.items?.pattern != null) {
      patternValidation = new RegExp(schema?.items?.pattern);
    }
    importedCodes.forEach((code) => {
      if (patternValidation == null || patternValidation.test(code)) {
        ipcCodes.push(code);
      }
    });

    const newIpcCodes = [...new Set(ipcCodes)];
    setIpcCodes(newIpcCodes);
    onChange(newIpcCodes);
    onRemoveDroppedFile(droppedFiles[0].name);
  };

  const ChipComponent = () => (
    <div className={classes.ipcCodes}>
      <div>
        <ChipInput
          className={classes.chipInput}
          data-testid="chip-input"
          blurBehavior="add"
          value={ipcCodes}
          onAdd={chip => handleAddChip(chip)}
          onDelete={(chip, index) => handleDeleteChip(chip, index)}
          disabled={isDisabled}
          error={rawErrors.length > 0}
          label={displayLabel}
          variant="outlined"
          fullWidth
          fullWidthInput
          InputProps={{
            notched: true,
            label: displayLabel,
          }}
        />
        {tooltip && <Tooltip className={classes.tooltip} title={<Trans i18nKey={tooltip} />} />}
      </div>
      {errorMessage && <AssistiveText severity="error">{errorMessage}</AssistiveText>}
    </div>
  );

  if (!fileImport) {
    return <ChipComponent />;
  }
  return (
    <Paper className={classes.paper}>
      <Typography className={classes.titleText}>{fileImport[uiSchemaProperties.UI_TITLE]}</Typography>
      <ChipComponent />
      <AttachmentUploader
        attachmentType={fileImport?.[uiSchemaProperties.ATTACHMENTS_CATEGORIES][0]}
        metaFieldNames={[]}
        disabledAttachmentTypes={[]}
        attachmentsMetadata={[]}
        mergedAttachmentsMetadata={[]}
        onFileDropped={() => {}}
        onDroppedFileRemoved={() => {}}
        onAttachmentUploaded={() => {}}
        uiSchema={{}}
        jsonSchema={{}}
        parentClasses={classes}
        readOnly={disabled}
        onConfirmUploadArg={handleCsvImport}
        confirmLabel={fileImport[uiSchemaProperties.CONFIRM_BUTTON]}
      />
    </Paper>
  );
};

ChipsField.defaultProps = {
  rawErrors: [],
  uiSchema: {},
  schema: {},
};
export default ChipsField;
